import { useEffect } from "react";
import { Youtube, Sparkles, Layers, Clapperboard, PenTool, Play } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";

const IMG = "https://images.unsplash.com/photo-1612544409025-e1f6a56c1152?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800&auto=format&fit=crop";

const channels = [
  {
    icon: Youtube,
    id: "youtube",
    title: "YouTube",
    status: "In Pre-Production",
    desc: "The ADD VALUE YouTube channel is in development — long-form video on building brands, business, and the philosophy behind the company.",
  },
  {
    icon: Sparkles,
    id: "ai",
    title: "AI Content",
    status: "Exploring",
    desc: "ADD VALUE is developing AI-assisted production — imagery, film, and new formats — as part of its future media output.",
  },
  {
    icon: Layers,
    id: "digital",
    title: "Digital Media",
    status: "In Development",
    desc: "Interactive and digital-first projects currently being designed inside the company.",
  },
  {
    icon: Clapperboard,
    id: "video",
    title: "Video Projects",
    status: "In Development",
    desc: "Short-form and editorial video produced under the ADD VALUE name. Releases will appear here as they launch.",
  },
  {
    icon: PenTool,
    id: "creative",
    title: "Creative Projects",
    status: "On the Horizon",
    desc: "Campaigns, visual concepts, and original creative work extending the world of ADD VALUE beyond its products.",
  },
];

const Media = () => {
  useEffect(() => {
    document.title = "Media — ADD VALUE HOLDINGS LLC";
  }, []);

  return (
    <div data-testid="media-page">
      <PageHero
        testid="media-hero"
        image={IMG}
        overline="Digital Media & Content"
        lines={["Stories, told", "with intention."]}
        lede="The media division of ADD VALUE — video, digital projects, and future productions, all built on the same standard as everything else bearing the name."
      />

      <section className="border-t border-[#1c1c1f]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <div>
              <Overline>Channels & Projects</Overline>
              <h2 className="mt-6 font-serif text-2xl tracking-tight text-white/95 sm:text-3xl lg:text-4xl">
                In production now.
              </h2>
            </div>
            <p className="max-w-sm text-sm font-light text-stone-500">
              This page grows with the company — new releases and videos will
              appear here as they launch.
            </p>
          </Reveal>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {channels.map((c, i) => (
              <Reveal key={c.id} delay={0.07 * i}>
                <div
                  data-testid={`media-card-${c.id}`}
                  className="group flex h-full flex-col border border-[#222226] bg-[#121214] transition-colors duration-500 hover:border-[#D4AF37]/40"
                >
                  <div className="relative flex aspect-video items-center justify-center overflow-hidden border-b border-[#222226] bg-[#0A0A0B]">
                    <Play className="h-8 w-8 text-stone-700 transition-colors duration-500 group-hover:text-[#D4AF37]" />
                    <span className="absolute left-4 top-4 border border-[#2a2a2e] bg-[#0A0A0B]/80 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-stone-400">
                      {c.status}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-8">
                    <c.icon className="h-5 w-5 text-[#D4AF37]" />
                    <h3 className="mt-5 font-serif text-2xl text-white">{c.title}</h3>
                    <p className="mt-3 text-sm font-light leading-relaxed text-stone-500">
                      {c.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}

            <Reveal delay={0.35}>
              <div className="flex h-full flex-col justify-between border border-dashed border-[#2a2a2e] bg-transparent p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500">
                  Next Release
                </p>
                <p
                  data-testid="media-next-release"
                  className="mt-10 font-serif text-2xl italic leading-snug text-stone-400"
                >
                  The first releases are being crafted. Watch this space.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Media;
